// components/AdminMainMenu.js
import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';

import { API_URL } from '../reusable/url';
import './adminMainMenu.css'

const AdminMainMenu = () => {
  const [users, setUsers] = useState([]);
  const [showUsers, setShowUsers] = useState(false);
  const [loading, setLoading] = useState(false);
  const [confirmUser, setConfirmUser] = useState(null); // { id, username } | null

  const authToken = useSelector((state) => state.user.token);

  const loadUsersFromServer = () => {
    if (!authToken) {
      toast.error('No auth token found – please log in again.');
      return;
    }

    setLoading(true);

    fetch(API_URL('list-users'), {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${authToken}`,
      },
      credentials: 'include',
    })
      .then(res => {
        if (!res.ok) {
          return res.json().catch(() => ({})).then(data => {
            throw new Error(data.message || `Failed to load users (status ${res.status})`);
          });
        }
        return res.json();
      })
      .then(data => {
        setUsers(data);
        setShowUsers(true);
        if (data.length === 0) {
          toast.info('No user accounts found.');
        }
      })
      .catch(err => {
        console.error(err);
        toast.error(err.message || 'Failed to load user accounts');
        setShowUsers(false);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  // Toggle handler: if list is visible, hide; if hidden, load & show
  const handleToggleUsers = (e) => {
    e.preventDefault();

    if (showUsers) {
      setShowUsers(false);
      return;
    }

    loadUsersFromServer();
  };

  const deleteUserOnServer = (userId, username) => {
    if (!authToken) {
      toast.error('No auth token found – please log in again.');
      return;
    }

    fetch(API_URL(`delete-user/${userId}`), {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${authToken}`,
      },
      credentials: 'include',
    })
      .then(res => {
        return res.json().catch(() => ({})).then(data => ({ res, data }));
      })
      .then(({ res, data }) => {
        if (!res.ok) {
          throw new Error(data.message || `Failed to delete user (status ${res.status})`);
        }

        setUsers(prev => prev.filter(u => u._id !== userId));
        toast.success(`User "${username}" deleted successfully.`);
      })
      .catch(err => {
        console.error(err);
        toast.error(err.message || 'Failed to delete user');
      });
  };

  const handleDeleteClick = (userId, username) => {
    setConfirmUser({ id: userId, username });
  };

  const handleConfirmDelete = () => {
    if (!confirmUser) return;
    deleteUserOnServer(confirmUser.id, confirmUser.username);
    setConfirmUser(null);
  };

  const handleCancelDelete = () => {
    setConfirmUser(null);
  };

  return (
    <div className="adminMainMenu-container">
      <h1>Administrator Main Menu</h1>

      <div>
        <Link to="/generate-user-token">
          <button className="btn-generate-user-token">
            Generate User Registration Token
          </button>
        </Link>
      </div>

      <div>
        <button
          className="btn-view-users"
          type="button"
          onClick={handleToggleUsers}
          disabled={loading}
        >
          {loading
            ? 'Loading user accounts…'
            : showUsers
              ? 'Hide user accounts'
              : 'View user accounts'}
        </button>
      </div>

      {showUsers && (
        <div className="user-list-container">
          <h2>Existing user accounts</h2>
          {users.length === 0 ? (
            <p className="admin-info">No user accounts found.</p>
          ) : (
            <ul className="user-list">
              {users.map(user => (
                <li key={user._id} className="user-list-item">
                  <span className="user-name">{user.username}</span>
                  <button
                    type="button"
                    className="btn-delete-user"
                    onClick={() => handleDeleteClick(user._id, user.username)}
                  >
                    Delete
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      {/* Custom confirmation dialog */}
      {confirmUser && (
        <div className="modal-backdrop">
          <div className="modal">
            <h3>Delete user</h3>
            <p>
              Are you sure you want to delete user{' '}
              <strong>{confirmUser.username}</strong>?<br />
              This action cannot be undone.
            </p>
            <div className="modal-buttons">
              <button
                type="button"
                className="btn-confirm-delete"
                onClick={handleConfirmDelete}
              >
                Yes, delete
              </button>
              <button
                type="button"
                className="btn-cancel-delete"
                onClick={handleCancelDelete}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminMainMenu;
